import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, Input, LOCALE_ID, OnInit, ViewChild } from '@angular/core';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { PORTAL_TASK_STATUS_ARRAY, TaskOverview } from '@apps/fiduciary/fiduciary.state';
import { TaskDisplayMode } from '@apps/fiduciary/tasks/tasks.model';
import { IOptionsStore, OptionsStoreService, UiTableKeys } from '@platform/components/exper-table/sort-options-store.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { User } from '@core/core.state';
import { CommonModule, formatDate } from '@angular/common';
import { getDateAsNumber } from '@core/customer-space-admin/utils/cs-user.utils';
import { EmptyListComponent } from '@platform/components/empty-list/empty-list.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { TaskTableItemTitleComponent } from '../task-table-item-title/task-table-item-title.component';

const taskDisplayModeColumnMapping: Record<TaskDisplayMode, string> = {
  AssignedToCurrentUser: 'createdBy',
  CreatedByCurrentUser: 'assignedTo',
};

@Component({
  selector: 'app-task-table',
  templateUrl: './task-table.component.html',
  styleUrls: ['./task-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatTooltipModule,
    RouterModule,
    TranslateModule,
    TaskTableItemTitleComponent,
    EmptyListComponent,
  ],
})
export class TaskTableComponent implements OnInit, AfterViewInit {
  @Input() showCustomer!: boolean;
  @Input() displayMode!: TaskDisplayMode;

  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

  public dataSource = new MatTableDataSource<TaskOverview>();

  private readonly optionsStore: IOptionsStore;

  constructor(
    optionsStoreService: OptionsStoreService,
    private translate: TranslateService,
    @Inject(LOCALE_ID) private locale: string,
  ) {
    this.optionsStore = optionsStoreService.getOptionsStore(UiTableKeys.TaskList);
  }

  public get columns() {
    return [
      ...(this.showCustomer ? ['customer'] : []),
      ...['title', 'status', 'dueOn', 'createdOn', taskDisplayModeColumnMapping[this.displayMode], 'priority'],
    ];
  }

  @Input() set tasks(value: TaskOverview[]) {
    this.dataSource.data = value;
  }

  @Input() set filter(value: string) {
    this.dataSource.filter = value;
  }

  ngOnInit(): void {
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'title': {
          return item.title.toLocaleLowerCase();
        }
        case 'assignedTo': {
          return item.assignees[0].name.toLocaleLowerCase();
        }
        case 'createdBy': {
          return item.createdBy.name;
        }
        case 'status': {
          return this.getTranslatedStatus(item);
        }
        case 'customer': {
          return item.customerSpaceName.toLocaleLowerCase();
        }
        case 'createdOn': {
          return getDateAsNumber(item.createdOn);
        }
        case 'dueOn': {
          return getDateAsNumber(item.dueOn);
        }
        default: {
          return item[property as keyof TaskOverview]?.toString() ?? '';
        }
      }
    };

    this.dataSource.filterPredicate = (data, filter: string) => {
      if (!filter) {
        return true;
      }

      const customer = this.showCustomer ? data.customerSpaceName : '';
      const creatorOrAssignee =
        this.displayMode === 'CreatedByCurrentUser'
          ? data.createdBy.name === undefined
            ? data.createdBy
            : data.createdBy.name
          : data.assignees.map((item) => item.name).join();

      const dueOnString = data.dueOn ? formatDate(data.dueOn, 'mediumDate', this.locale) : '';
      const createdOnString = formatDate(data.createdOn, 'mediumDate', this.locale);
      const concatObject = `${data.title}${this.getTranslatedStatus(data)}${dueOnString}${createdOnString}${creatorOrAssignee}${customer}`;
      return concatObject.toLowerCase().indexOf(filter.toLowerCase()) >= 0;
    };
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;

    this.optionsStore.registerSort(this.sort);
    this.optionsStore.registerPaginatorPageSize(this.paginator);
  }

  public getTranslatedStatus(task: TaskOverview): string {
    if (!PORTAL_TASK_STATUS_ARRAY.includes(task.status)) {
      return task.status;
    }
    return this.translate.instant(`FIDUCIARY.tasks.status.${task.status.toLocaleLowerCase()}`);
  }

  public isOverdue(task: TaskOverview): boolean {
    if (task.status === 'Done') {
      return false;
    }
    const dateTask = moment(task.dueOn);
    const dateNow = moment(new Date().setHours(0, 0, 0, 0));
    return moment(dateNow).isAfter(dateTask);
  }

  public getAssignedNames(users: User[]) {
    return users.map((item) => item.name).join(', ');
  }
}
